import { Injectable, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { ToastService } from '../../services/toast.service';
import { ApiResponse } from '../../models/api-response.model';
import { environment } from '../../../environments/environment';
import { getDeviceId } from './device-id';

interface EstadoSesion {
  vigente: boolean;
  motivo?: string;
  sesiones_activas?: number;
  max_sesiones?: number;
}

/**
 * Límite de sesiones concurrentes por usuario (2). El backend lleva el registro;
 * aquí registramos esta sesión al entrar y latimos periódicamente para detectar
 * que otro dispositivo nos desplazó, en cuyo caso se cierra sesión local.
 */
@Injectable({ providedIn: 'root' })
export class SessionLimitService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);

  private readonly url = `${environment.apiUrl}/auth/sesiones`;
  private readonly LATIDO_MS = 2 * 60 * 1000;

  private timer: ReturnType<typeof setInterval> | null = null;

  /** Cuántas de las sesiones permitidas están en uso (para mostrarlo si hace falta). */
  sesionesActivas = signal(0);

  /** Da de alta esta sesión tras un login exitoso; desplaza la más antigua si ya había 2. */
  async registrar(): Promise<void> {
    try {
      const res = await lastValueFrom(
        this.http.post<ApiResponse<{ sesiones_activas: number; sesiones_cerradas: number }>>(
          this.url,
          { device_id: getDeviceId(), user_agent: navigator.userAgent }
        )
      );
      this.sesionesActivas.set(res.data?.sesiones_activas ?? 1);
      if (res.data?.sesiones_cerradas) {
        this.toast.info('Se cerró tu sesión más antigua: se permiten 2 sesiones por usuario.');
      }
    } catch {
      // Un fallo aquí no debe impedir trabajar: el latido reintenta el alta.
    }
  }

  /** Arranca el latido. Idempotente: reinicia el intervalo si ya estaba corriendo. */
  iniciarMonitoreo(): void {
    this.detenerMonitoreo();
    void this.verificar();
    this.timer = setInterval(() => void this.verificar(), this.LATIDO_MS);
  }

  detenerMonitoreo(): void {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }
  }

  private async verificar(): Promise<void> {
    if (!this.authService.currentUser()) {
      this.detenerMonitoreo();
      return;
    }

    try {
      const res = await lastValueFrom(
        this.http.get<ApiResponse<EstadoSesion>>(
          `${this.url}?device_id=${encodeURIComponent(getDeviceId())}`
        )
      );
      const estado = res.data;
      if (!estado) return;

      this.sesionesActivas.set(estado.sesiones_activas ?? this.sesionesActivas());

      if (!estado.vigente) {
        this.detenerMonitoreo();
        this.toast.warning(
          'Tu sesión se cerró porque se inició sesión en otro dispositivo (máximo 2 por usuario).'
        );
        await this.authService.logout();
        this.router.navigate(['/auth/login']);
      }
    } catch {
      // Errores de red no cierran sesión: se reintenta en el siguiente latido.
    }
  }
}
